import { useCallback, useState } from 'react';
import { history } from '@umijs/max';
import { useAliveController } from 'react-activation';
import { getRouteTitle, getViewPath, isAffixPath, normalizePathname } from '@/utils/routeUtil';

export interface TagView {
  /** 唯一标识，pathname + search */
  path: string;
  pathname: string;
  search: string;
  title: string;
  affix?: boolean;
}

function createHomeTag(): TagView {
  return {
    path: '/home',
    pathname: '/home',
    search: '',
    title: getRouteTitle('/home'),
    affix: true,
  };
}

export default () => {
  const { dropScope } = useAliveController();
  const [visitedViews, setVisitedViews] = useState<TagView[]>([createHomeTag()]);

  const addView = useCallback((payload: { pathname: string; search?: string; title: string }) => {
    const pathname = normalizePathname(payload.pathname);
    const search = payload.search || '';
    const path = getViewPath(pathname, search);
    const title = getRouteTitle(pathname);

    setVisitedViews((prev) => {
      const cleaned = prev.filter((item) => item.pathname !== '/' && item.path !== '/');
      const existingExact = cleaned.find((item) => item.path === path);
      if (existingExact) {
        if (existingExact.title === title) {
          return cleaned;
        }
        return cleaned.map((item) => (item.path === path ? { ...item, title } : item));
      }

      // 同一 pathname 只保留一个标签：search 变化时丢掉旧缓存并替换
      const samePathname = cleaned.filter((item) => item.pathname === pathname);
      samePathname.forEach((item) => {
        dropScope(item.path);
      });
      const withoutSamePathname = cleaned.filter((item) => item.pathname !== pathname);

      return [
        ...withoutSamePathname,
        {
          path,
          pathname,
          search,
          title,
          affix: isAffixPath(pathname),
        },
      ];
    });
  }, [dropScope]);

  const refreshAllTitles = useCallback(() => {
    setVisitedViews((prev) =>
      prev.map((item) => {
        const title = getRouteTitle(item.pathname);
        return item.title === title ? item : { ...item, title };
      }),
    );
  }, []);

  const closeView = useCallback((targetPath: string, activePath: string) => {
    setVisitedViews((prev) => {
      const target = prev.find((item) => item.path === targetPath);
      if (!target || target.affix) {
        return prev;
      }

      dropScope(targetPath);

      const nextViews = prev.filter((item) => item.path !== targetPath);
      if (activePath === targetPath) {
        const lastView = nextViews[nextViews.length - 1] || createHomeTag();
        history.push(getViewPath(lastView.pathname, lastView.search));
      }
      return nextViews;
    });
  }, [dropScope]);

  const closeOtherViews = useCallback((keepPath: string) => {
    setVisitedViews((prev) => {
      prev.forEach((item) => {
        if (item.path !== keepPath && !item.affix) {
          dropScope(item.path);
        }
      });
      return prev.filter((item) => item.affix || item.path === keepPath);
    });
  }, [dropScope]);

  const closeAllViews = useCallback(() => {
    setVisitedViews((prev) => {
      prev.forEach((item) => {
        if (!item.affix) {
          dropScope(item.path);
        }
      });
      const affixViews = prev.filter((item) => item.affix);
      const homeView = affixViews[0] || createHomeTag();
      history.push(getViewPath(homeView.pathname, homeView.search));
      return affixViews.length ? affixViews : [createHomeTag()];
    });
  }, [dropScope]);

  return {
    visitedViews,
    addView,
    refreshAllTitles,
    closeView,
    closeOtherViews,
    closeAllViews,
  };
};
